import { Outlet } from "@tanstack/react-router";
import { type ReactNode, useSyncExternalStore } from "react";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { DesktopOnly } from "./desktop-only";
import { Header } from "./header";
import { Sidebar } from "./sidebar";

// 1024px = Tailwind's `lg` breakpoint (the plan's Global Constraints:
// "supported viewports >=1024px; below that render a 'desktop only'
// screen"). Tracked via a `resize` listener + `window.innerWidth` rather
// than `matchMedia` — simpler to drive from tests (set `innerWidth`, fire a
// `resize` event) and happy-dom implements both reliably.
const NARROW_BREAKPOINT_PX = 1024;

function isNarrow(): boolean {
    return typeof window !== "undefined" && window.innerWidth < NARROW_BREAKPOINT_PX;
}

function useIsNarrowViewport(): boolean {
    return useSyncExternalStore(
        (onChange) => {
            if (typeof window === "undefined") return () => {};
            window.addEventListener("resize", onChange);
            return () => window.removeEventListener("resize", onChange);
        },
        isNarrow,
        () => false,
    );
}

interface AppShellProps {
    children?: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
    const narrow = useIsNarrowViewport();
    const { workspaceName } = useWorkspaceContext();

    if (narrow) {
        return <DesktopOnly workspaceName={workspaceName} />;
    }

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-background">
            <Header />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main
                    id="main-content"
                    tabIndex={-1}
                    // Still scrollable: the detail pages and forms are taller
                    // than the viewport and should scroll. The list pages pin
                    // themselves to it with h-full instead, so they never
                    // reach for this scrollbar.
                    //
                    // `scrollbar-gutter: stable` reserves the gutter even when
                    // the content fits, so navigating from a list (fits, no
                    // scrollbar) to a detail page (scrolls) doesn't shift the
                    // content sideways by the scrollbar's width. Ported from
                    // the vouchers admin, which documents the same jump.
                    className="flex-1 overflow-y-auto p-6 [scrollbar-gutter:stable]"
                >
                    {children ?? <Outlet />}
                </main>
            </div>
        </div>
    );
}
